import { useCallback, useEffect, useState } from "react";
import { formatDate } from "./core";
import type { StaffPaneProps } from "./console-types";

// "Přegenerovat prompt" (Tomáš Pohl 2026-10-05). The run lives on the Mac
// Mini and takes tens of minutes; this card only starts it, shows the honest
// state and, on a hand-edited prompt, asks before the second click overwrites.
// Rendered in the client tools AND beside the beta podněty (Tomáš's ask).

type RebuildState = "idle" | "queued" | "running" | "done" | "failed" | "unavailable";

interface RebuildStatus {
  state: RebuildState;
  startedAt: string | null;
  finishedAt: string | null;
  result: {
    promptChars: number | null;
    transcriptCount: number | null;
    openQuestions: number | null;
    systemPromptDocUrl: string | null;
    onboardingDocUrl: string | null;
  } | null;
  errorReason: string | null;
}

const KNOWN_REASONS = ["doc_edited_by_human", "already_running", "not_enough_sources", "no_instance", "no_workspace", "unavailable"];
const POLL_MS = 15_000;

function reasonKey(reason: string | null | undefined): string {
  return `staffConsole.promptRebuild.reasons.${reason && KNOWN_REASONS.includes(reason) ? reason : "unavailable"}`;
}

export function PromptRebuildCard(props: StaffPaneProps) {
  const clientId = props.detail.client.id;
  const t = props.t;
  const [status, setStatus] = useState<RebuildStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const response = await fetch(`/api/staff/clients/${encodeURIComponent(clientId)}/prompt-rebuild`, { credentials: "include" });
      if (!response.ok) { setStatus((prev) => prev ?? { state: "unavailable", startedAt: null, finishedAt: null, result: null, errorReason: null }); return; }
      setStatus(await response.json() as RebuildStatus);
    } catch {
      setStatus((prev) => prev ?? { state: "unavailable", startedAt: null, finishedAt: null, result: null, errorReason: null });
    }
  }, [clientId]);

  useEffect(() => { void load(); }, [load]);
  const active = status?.state === "queued" || status?.state === "running";
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => { void load(); }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [active, load]);

  async function launch(confirmOverwrite: boolean) {
    setBusy(true);
    setRefusal(null);
    try {
      const response = await props.run(`/api/staff/clients/${encodeURIComponent(clientId)}/prompt-rebuild`,
        { method: "POST", body: { confirmOverwrite } }, { refresh: false });
      const body = await response.json().catch(() => null) as { ok?: boolean; reason?: string } | null;
      if (response.ok && body?.ok) { await load(); return; }
      setRefusal(body?.reason ?? "unavailable");
    } finally {
      setBusy(false);
    }
  }

  const state: RebuildState = status?.state ?? "unavailable";
  const result = status?.result;
  const askOverwrite = refusal === "doc_edited_by_human";
  return (
    <article className="staff-card" data-prompt-rebuild="true">
      <h3>{t("staffConsole.promptRebuild.title")}</h3>
      <p className="staff-empty">{t("staffConsole.promptRebuild.hint")}</p>
      {status && <p data-prompt-rebuild-state={state}>{t(`staffConsole.promptRebuild.states.${state}`)}</p>}
      {state === "failed" && status?.errorReason && <p className="message error-message">{t(reasonKey(status.errorReason))}</p>}
      {state === "done" && result && (
        <>
          <p>
            {t("staffConsole.promptRebuild.summary")
              .replace("{transcripts}", String(result.transcriptCount ?? "?"))
              .replace("{chars}", String(result.promptChars ?? "?"))
              .replace("{questions}", String(result.openQuestions ?? "?"))}
          </p>
          {status?.finishedAt && <p className="staff-empty">{t("staffConsole.promptRebuild.finishedAt").replace("{date}", formatDate(status.finishedAt, props.locale))}</p>}
          <p>
            {result.systemPromptDocUrl && <a href={result.systemPromptDocUrl} target="_blank" rel="noreferrer">{t("staffConsole.promptRebuild.promptDoc")}</a>}
            {result.systemPromptDocUrl && result.onboardingDocUrl && " · "}
            {result.onboardingDocUrl && <a href={result.onboardingDocUrl} target="_blank" rel="noreferrer">{t("staffConsole.promptRebuild.onboardingDoc")}</a>}
          </p>
        </>
      )}
      {refusal && <p className="message error-message" role="alert">{t(reasonKey(refusal))}</p>}
      <div className="button-row">
        {askOverwrite ? (
          <>
            <button type="button" className="primary-button compact-button" disabled={busy} onClick={() => void launch(true)}>
              {busy ? t("staffConsole.promptRebuild.starting") : t("staffConsole.promptRebuild.confirmOverwrite")}
            </button>
            <button type="button" className="secondary-button compact-button" disabled={busy} onClick={() => setRefusal(null)}>
              {t("staffConsole.promptRebuild.cancel")}
            </button>
          </>
        ) : (
          <button type="button" className="primary-button compact-button" disabled={busy || active} onClick={() => void launch(false)}>
            {busy ? t("staffConsole.promptRebuild.starting") : t("staffConsole.promptRebuild.launch")}
          </button>
        )}
      </div>
    </article>
  );
}
